import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CollectionHelper, CompareHelper, ConvertHelper, GUID, ObjectHelper } from '@barthauer/abc/core';
import {
  CacheStorageAdapter,
  CoreDataEntityHelper,
  DataService as CoreDataService,
  ObjectKey,
  ObjectSpaceService,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  ReadListDataSourceResult,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { DataEntity, MapService, ObjectTypesHelper } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  DialogResult,
  LoadProtocolTaskInfoSnapshot,
  MessageBoxButtons,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { ParameterHelper } from '@barthauer/abc/queries/parameters/core';
import {
  BasysMapFrameService,
  BooleanSetting,
  LibService,
  RouteConfiguration,
  RoutingService,
  ViewStateChangeArgType,
} from '@barthauer/basys/map-frame';
import {
  CreateTicketService,
  MtBase as ImMtBase,
  MtObject as ImMtObject,
  MtWork as ImMtWork,
  MtWorkType as ImMtWorkType,
  MtWorkTypeSimple as ImMtWorkTypeSimple,
  ImWorkService,
  MtBase,
  MtTaskType,
  WorkService,
  WorkWizardOptions,
} from '@barthauer/basys/opera/wizards';
import { CommentsService } from 'libs/abc/common-editors/src/lib/services/comments.service';
import {
  MtTicketCreation,
  MtTicketCreationDocument,
} from 'libs/basys/opera/wizards/src/lib/create-works-wizard/models/tickets.model';
import { forkJoin, from, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { MtObject, MtPositionType, MtTask, MtWork } from '../models/mtData.model';
import { MtResource, MtResourceType } from '../models/mtData.resources.model';
import { MtTicket } from '../models/mtData.tickets.model';
import { ResourceListService } from '../resources/resource-list/resource-list.service';
import { ResourcesConstants, ResourceTypeViewPackage } from '../resources/resources.model';
import { MTHelper, ViewModelHelper } from '../shared/helpers';
import { SaveDialogComponent } from '../shared/save-dialog.component';
import { TicketConstants, TicketViewPackage } from '../tickets/tickets.model';
import { MobileResourceTypeViewModel, MobileResourceViewModel } from '../viewModels/mobileData.resources.viewModel';
import { MobileTicketTaskViewModel, MobileTicketViewModel } from '../viewModels/mobileData.tickets.viewModel';
import {
  ListDataSource,
  ListDataSourceItem,
  MobileObjectViewModel,
  MobileWorkViewModel,
} from '../viewModels/mobileData.viewModel';
import { WorkConstants, WorkViewMode, WorkViewPackage } from '../works/works.model';
import { AppService } from './app.service';
import { DataService } from './data.service';
import { MobileDocumentViewModel } from '../viewModels/mobileData.common.viewModel';

interface State {
  mtObjects: MtObject[];
  indexedMtPositionTypes: {};
  mtTickets: MtTicket[];
  mtTicketResources: MtResource[];
  mtTicketTaskStatusList: ReadListDataSourceResult;
  mtResourceTypes: MtResourceType[];
  mtWorkTypes: ImMtWorkType[];
  mtTicketDringlichkeitList: ReadListDataSourceResult;
  mtTicketKategorieList: ReadListDataSourceResult;
  mtCurrentUserResourceId: string;
}

declare let window: any;
declare let cordova: any;
@Injectable()
export class FrameService {
  private mobileWorksSubject = new Subject<WorkViewPackage>();
  public mobileTicketsSubject = new Subject<TicketViewPackage>();
  private mobileTicketResources = new Array<MobileResourceViewModel>();
  private mobileResourceTypesSubject = new Subject<ResourceTypeViewPackage>();
  private dynSubscriptions: Subscription[] = [];
  private syncOfflineDataTask: LoadProtocolTaskInfoSnapshot;
  private state: State;
  private hasPendingChanges: {} = {};
  private isSaveDialogOpened: boolean = false;
  public workViewPackage: WorkViewPackage;
  public ticketViewPackage: TicketViewPackage;
  public resourceTypeViewPackage: ResourceTypeViewPackage;
  public orderSelectionRoute: RouteConfiguration;
  public resAsync: Promise<ResourceSet>;
  public bottomSheetTogglePreparations: () => void;
  public bottomSheetToggleCondition: (target?: string) => boolean;
  private uploadFileDocumentsTaskInfos: Map<MobileDocumentViewModel, LoadProtocolTaskInfoSnapshot> = new Map<
    MobileDocumentViewModel,
    LoadProtocolTaskInfoSnapshot
  >();

  constructor(
    public dataService: DataService,
    private persistenceService: PersistenceService,
    private objectSpaceService: ObjectSpaceService,
    private mapService: MapService,
    private resourceService: ResourceManagerService,
    private snackBar: MatSnackBar,
    private libService: LibService,
    private frame: BasysMapFrameService,
    private imWorkService: ImWorkService,
    private createTicketService: CreateTicketService,
    private messageBoxService: MessageBoxService,
    private routingService: RoutingService,
    private saveDialog: MatDialog,
    private commentsService: CommentsService,
    private coreDataService: CoreDataService,
    private resourceListService: ResourceListService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('abc-maintenance-opera');

    this.hasPendingChanges[WorkConstants.PersistenceCategory_WorkDetail] = false;
    this.hasPendingChanges[WorkConstants.PersistenceCategory_TaskDetail] = false;
    this.hasPendingChanges[TicketConstants.PersistenceCategory_TicketDetail] = false;
    this.hasPendingChanges[ResourcesConstants.PersistenceCategory_ResourceEvent] = false;

    this.orderSelectionRoute = frame.createRouteConfig(
      AppService.Route_WorkModeSelection,
      AppService.Route_WorkModeSelection,
      'mainOutlet',
    );
    this.dataService.notify = (msg: string) => this.openSnackBar(msg);
    this.dataService.cacheRefreshed = (state) => {
      this.refreshMobileWorksCore(state, null, null, true);
      this.refreshMobileTicketsCore(state, null, null, true);
      this.refreshMobileResourceTypesCore(state, null, null, true);
    };

    this.dataService.pendingObjectsChanged$.subscribe((poc) => {
      this.hasPendingChanges[poc.category] = poc.hasAnyChanges;
    });

    this.frame.isBottomSheetToggleAllowed = async () => {
      if (this.bottomSheetTogglePreparations) {
        this.bottomSheetTogglePreparations();
      }
      let allowed: boolean = true;
      if (this.bottomSheetToggleCondition) {
        allowed = this.bottomSheetToggleCondition();
      }
      if (allowed) {
        await this.ensurePersistingState(
          () => {},
          () => (allowed = false),
        );
      }
      return allowed;
    };

    this.mapService.onMapShortMessageReceived().subscribe((message) => {
      this.openSnackBar(message);
    });

    this.imWorkService.onImWorkCreated.subscribe((e) => this.showImmediateWork(e.workId, e.show));

    this.imWorkService.persistPendingChanges = (category, errorCallback) =>
      this.dataService.persistPendingChanges(category, errorCallback);

    this.createTicketService.refreshTickets = async (forceRefresh) => {
      await this.dataService.getMtTickets(forceRefresh).toPromise();
    };
    this.createTicketService.onTicketCreated.subscribe((e) => {
      if (CacheStorageAdapter.useOfflineMode) {
        this.showTicket(e.ticketDesignation, e.show);
      } else {
        if (e.entity) {
          this.mapService.refreshMapLayer([e.entity]);
        } else {
          this.mapService.refreshMapLayer([{ id: e.ticketId, objectType: 'MtTicket' }]);
        }
        this.showTicket(e.ticketDesignation, e.show);
        this.refreshMobileTickets(undefined, true);
      }
    });

    this.commentsService.persistPendingChanges = (c) => this.dataService.persistPendingChanges(c);

    this.libService.preferMtObjectDocuments = true;
  }

  public async ensurePersistingState(callback: () => void, cancelCallback?: () => void) {
    const current = this.routingService.getCurrentConfig();

    let persistenceCategory;
    switch (current.designation) {
      case AppService.Route_WorkDetail:
        persistenceCategory = WorkConstants.PersistenceCategory_WorkDetail;
        break;
      case AppService.Route_TaskDetail:
        persistenceCategory = WorkConstants.PersistenceCategory_TaskDetail;
        break;
      case AppService.Route_TicketDetail:
        persistenceCategory = TicketConstants.PersistenceCategory_TicketDetail;
        break;
      case AppService.Route_ResourceEventMaterialMaterialExpense:
      case AppService.Route_ResourceEventPersonnelWorkingTimes:
      case AppService.Route_ResourceEventVehicleDowntime:
      case AppService.Route_ResourceEventVehicleMileage:
      case AppService.Route_ResourceEventVehicleOperatingHours:
      case AppService.Route_ResourceEventVehicleWaterFilling:
      case AppService.Route_ResourceEventOtherOther:
        persistenceCategory = ResourcesConstants.PersistenceCategory_ResourceEvent;
        break;
    }
    if (!this.hasPendingChanges[persistenceCategory] || this.frame.mapInfo.isReadOnly) {
      callback();
    } else {
      if ((this.frame.frameConfig.settings.get(AppService.Setting_SaveChangesImmediately) as BooleanSetting).value) {
        this.dataService.persistPendingChanges(persistenceCategory);
        this.frame.refreshWidgets();
        callback();
      } else if (!this.isSaveDialogOpened) {
        this.isSaveDialogOpened = true;
        const dialogRef = this.saveDialog.open(SaveDialogComponent, {
          // FIXME hasBackdrop = true => Issue with arena overlay
          hasBackdrop: false,
          disableClose: true,
        });

        dialogRef.componentInstance.onSave = () => {
          this.dataService.persistPendingChanges(persistenceCategory);
          dialogRef.close();
          this.isSaveDialogOpened = false;
          callback();
        };
        dialogRef.componentInstance.onRevert = () => {
          this.dataService.revertPendingChanges(persistenceCategory);
          dialogRef.close();
          this.isSaveDialogOpened = false;
          callback();
        };
        dialogRef.componentInstance.onCancel = () => {
          dialogRef.close();
          this.isSaveDialogOpened = false;
          if (cancelCallback) {
            cancelCallback();
          }
        };

        await dialogRef.afterClosed().toPromise();
      }
    }
  }

  //works - start -
  refreshMobileWorks(afterRefreshMethod?: any, newWorkIds?: string[], closeBottomSheetAndRepublish?: boolean) {
    this.dynSubscriptions.push(
      this.dataService.loadState().subscribe(
        (state) => {
          this.refreshMobileWorksCore(
            state?.current,
            state?.last,
            afterRefreshMethod,
            closeBottomSheetAndRepublish,
            true,
            newWorkIds,
          );
        },
        (error) => {
          console.error(error);
          this.refreshMobileWorksCore();
        },
      ),
    );
  }

  private refreshMobileWorksCore(
    currentstate?: State,
    laststate?: State,
    afterRefreshMethod?: any,
    closeBottomSheetAndRepublish?: boolean,
    isRefresh?: boolean,
    newWorkIds?: string[],
  ) {
    this.dynSubscriptions.push(
      this.queryMobileWorks(currentstate, laststate, isRefresh, newWorkIds).subscribe(
        (mobileWorks) => {
          if (closeBottomSheetAndRepublish) {
            this.frame.lib.closeBottomSheet();
          }

          this.setupWorkViewPackage(mobileWorks);

          if (closeBottomSheetAndRepublish) {
            this.sendMobileWorks();
          }
          this.frame?.detectChanges();
          if (afterRefreshMethod) {
            afterRefreshMethod();
          }
        },
        (error) => {
          console.error(error);
          this.openSnackBar(this.frame.error.getFullErrorMessage(error, false), isRefresh ? 10000 : 2000);
          if (!isRefresh) {
            this.frame.routing.navigate(this.orderSelectionRoute);
          }
        },
      ),
    );
  }

  private setupWorkViewPackage(mobileWorks: MobileWorkViewModel[]): void {
    this.workViewPackage = new WorkViewPackage();
    this.workViewPackage.mode = WorkViewMode.AllFilteredByEntities;
    this.workViewPackage.allWorks = mobileWorks;
    this.setupOfflineState();
    this.frame.refreshWidgets();
  }

  private setupOfflineState(): void {
    this.dataService.setStateToPersist(this.state);
    this.dataService.refreshState = () => {
      this.refreshMobileWorksCore(undefined, undefined, undefined, undefined, true);
      this.refreshMobileTicketsCore(undefined, undefined, undefined, undefined, true);
      this.refreshMobileResourceTypesCore(undefined, undefined, undefined, undefined, true);
    };
  }

  sendMobileWorks() {
    if (this.workViewPackage) {
      this.mobileWorksSubject.next(this.workViewPackage);
    }
  }

  onMobileWorksSent(): Observable<WorkViewPackage> {
    return this.mobileWorksSubject.asObservable();
  }
  queryMobileWorks(
    currentstate: State,
    laststate: State,
    refreshCache?: boolean,
    newWorkIds?: string[],
  ): Observable<MobileWorkViewModel[]> {
    return from(this.queryMobileWorksAsync(currentstate, laststate, refreshCache, newWorkIds));
  }

  private async queryMobileWorksAsync(
    currentstate: State,
    laststate: State,
    refreshCache?: boolean,
    newWorkIds?: string[],
  ): Promise<MobileWorkViewModel[]> {
    const loadMobileWorksTask = from(this.resAsync.then((_) => _.get('Works-LoadingMessage'))).pipe(
      map((txt) => this.frame.loadingScreenService.addNewTask(txt, true)),
    );

    let loadProtocolTask: LoadProtocolTaskInfoSnapshot;
    try {
      const mtPositionTypesQuery = currentstate?.indexedMtPositionTypes
        ? of(currentstate?.indexedMtPositionTypes)
        : CacheStorageAdapter.useOfflineMode && laststate?.indexedMtPositionTypes
          ? of(laststate?.indexedMtPositionTypes)
          : this.dataService.getIndexedMtPositionTypes(refreshCache);

      let indexedMtPositionTypes = await mtPositionTypesQuery.toPromise();

      const mtObjectsQuery = currentstate?.mtObjects
        ? of(currentstate?.mtObjects)
        : CacheStorageAdapter.useOfflineMode && laststate?.mtObjects
          ? of(laststate?.mtObjects)
          : this.dataService.getMtObjects(refreshCache, undefined, indexedMtPositionTypes);

      const info = await this.libService.frame.getLoginInfo().toPromise();
      if (!info?.isAnonymous && !info?.userName) {
        return [];
      } else {
        const mtObjects: MtObject[] = await mtObjectsQuery.toPromise();
        loadProtocolTask = await loadMobileWorksTask.toPromise();
        if (newWorkIds?.length) {
          let found = 0;
          let entities: DataEntity[] = [];
          for (const o of mtObjects) {
            const oldfound = found;
            for (const w of o.Works) {
              const i = newWorkIds.findIndex((_) => w.Id === _);
              if (i >= 0) {
                found++;
              }
            }
            if (oldfound < found) {
              entities.push(new DataEntity(o.GeObjectType, o.GeObjectId));
            }
          }
          if (found < newWorkIds.length) {
            await this.saveOfflineData(false);
            const mtNewObjects = await this.dataService
              .getMtObjects(refreshCache, newWorkIds, indexedMtPositionTypes)
              .toPromise();
            entities = [];

            for (const newObject of mtNewObjects) {
              let added = false;
              for (const oldObject of mtObjects) {
                if (oldObject.Id === newObject.Id) {
                  for (let w of newObject.Works) {
                    oldObject.Works.push(w);
                    added = true;
                  }
                  entities.push(new DataEntity(newObject.GeObjectType, newObject.GeObjectId));
                  break;
                }
              }
              if (!added) {
                entities.push(new DataEntity(newObject.GeObjectType, newObject.GeObjectId));
                mtObjects.push(newObject);
              }
            }

            indexedMtPositionTypes = await this.dataService.getIndexedMtPositionTypes(refreshCache).toPromise();
          }
          this.dataService.redrawEntities(entities);
        }

        this.updateState({
          mtObjects: mtObjects,
          indexedMtPositionTypes: indexedMtPositionTypes,
          mtTickets: undefined,
          mtTicketResources: undefined,
          mtTicketTaskStatusList: undefined,
          mtResourceTypes: undefined,
          mtWorkTypes: undefined,
          mtTicketDringlichkeitList: undefined,
          mtTicketKategorieList: undefined,
          mtCurrentUserResourceId: undefined,
        });

        const result: MobileWorkViewModel[] = [];
        if (mtObjects && mtObjects.length > 0) {
          for (const o of mtObjects) {
            for (const mtWork of o.Works) {
              const w = new MobileWorkViewModel((await this.resAsync).get('Object'));
              await this.loadWork(w, mtWork, indexedMtPositionTypes);
              result.push(w);
            }
          }
        }
        return result;
      }
    } finally {
      if (loadProtocolTask?.id) {
        this.frame.loadingScreenService.finishTask(loadProtocolTask.id);
      }
    }
  }

  private async loadWork(mWork: MobileWorkViewModel, mtWork: MtWork, indexedMtPositionTypes: {}): Promise<void> {
    await mWork.load(
      this.persistenceService,
      this.objectSpaceService,
      mtWork,
      (positionTypeId) => indexedMtPositionTypes[positionTypeId][0].ResultTypes,
      (positionTypeId, resultTypeId) => {
        const posType: MtPositionType = indexedMtPositionTypes[positionTypeId][0];
        return posType.DefaultResultTypes?.find((_) => _.Id === resultTypeId) ? true : false;
      },
      (positionTypeId, resultTypeId) => {
        const posType: MtPositionType = indexedMtPositionTypes[positionTypeId][0];
        return posType.AutoCompleteResultTypes?.find((_) => _.Id === resultTypeId) ? true : false;
      },
      (positionTypeId, resultTypeId) => {
        const posType: MtPositionType = indexedMtPositionTypes[positionTypeId][0];
        return posType.SortOrders?.find((_) => _.type.Id === resultTypeId)?.index;
      },
      (positionTypeId, resultTypeId) => {
        const posType: MtPositionType = indexedMtPositionTypes[positionTypeId][0];
        return posType.Designations?.find((_) => _.type.Id === resultTypeId)?.designation;
      },
      (positionTypeId, resultTypeId) => {
        const posType: MtPositionType = indexedMtPositionTypes[positionTypeId][0];
        return posType.FollowingTaskTypeIds?.find((_) => _.type.Id === resultTypeId)?.taskTypeId;
      },
      (positionTypeId, resultTypeId) => {
        if (mtWork?.Object?.InvisibleValues) {
          return ConvertHelper.toBoolean(mtWork?.Object.InvisibleValues[positionTypeId + resultTypeId]);
        }
        return undefined;
      },
    );
  }

  private updateState(state: State) {
    if (this.state) {
      ObjectHelper.mergeInto(this.state, state);
    } else {
      this.state = state;
    }

    if (this.state?.mtWorkTypes?.length && this.state?.mtObjects) {
      this.imWorkService.getWorkTypesWithDateFilter = async (entity) =>
        //await this.getWorkTypeWithDateFilter(this.state?.mtWorkTypes, this.state?.mtObjects, entity);
        await this.getWorkTypeWithDateAndObjectTypeDefinionFilter(
          this.state?.mtWorkTypes,
          this.state?.mtObjects,
          entity,
        );
      this.imWorkService.generateWorksForEntity = async (entity, options) =>
        await this.generateWorksForEntity(entity, options);
    } else {
      this.imWorkService.getWorkTypesWithDateFilter = null;
      this.imWorkService.generateWorksForEntity = null;
    }

    this.createTicketService.generateTicketForEntity = async (ticketCreation, entity) =>
      await this.generateTicketForEntity(ticketCreation, entity);
  }

  private async generateWorksForEntity(
    entity: DataEntity,
    options: WorkWizardOptions,
  ): Promise<{ generatedWorks: ImMtWork[]; excludedObjects: ImMtBase[] }> {
    const res = await this.resAsync;
    const generatedWorks: ImMtWork[] = [];
    const excludedObjects: ImMtBase[] = [];

    const acceptOfflineStorage = this.dataService.getAcceptOfflineStorage();
    let mtObject = this.state?.mtObjects?.find((_) => _.GeObjectId === entity.id);

    if (!mtObject && !CacheStorageAdapter.useOfflineMode) {
      const query = await WorkService.createInsertMtObjectQueryFromEntity(
        this.objectSpaceService,
        this.coreDataService,
        entity,
        PersistMode.Merge,
      );

      mtObject = this.addObjectFromQuery(query.Object.TargetColumns, this.state?.mtObjects);
      const result = ConvertHelper.arrayBufferToObject<MtBase>(
        await this.coreDataService.executePersistObjectQuery(query).toPromise(),
      );
      if (result?.Id) {
        mtObject.Id = result.Id;
      }
      this.persistenceService.executePersistObjectQuery(query, acceptOfflineStorage).subscribe();
    } else if (!mtObject) {
      throw new Error(res.get('Work-WO_Object_Not_Created'));
    }

    if (mtObject) {
      const mtWorkType = this.state?.mtWorkTypes?.find((_) => _.Id === options.workTypeId);
      if (mtWorkType) {
        if (!options.objectInfos?.length) {
          options.objectInfos = [
            {
              MtObjectId: mtObject.Id,
              EarliestWorkExecDate: options.executeEarliest,
              LatestWorkExecDate: options.executeLatest,
              ScheduledWorkExecDate: options.scheduledExecutionDate,
              WorkExecTime: options.execTimeInDays,
            },
          ];
        }
        const taskQueries: PersistObjectsModel[] = [];
        const works: MtWork[] = [];
        const query = WorkService.createWorkPersistQueryForWorkType(
          options.objectInfos,
          options.state,
          mtWorkType,
          options.orderId,
          options.source,
          [],
          (targetColumns, workType) => {
            const mtWork = this.addWorkFromQuery(mtObject, targetColumns, workType);
            if (mtWork) {
              const imobject: ImMtObject = {
                Id: mtObject.Id,
                ObjectType: WorkService.MtObjectObjTypeName,
                GeObjectId: mtObject.GeObjectId,
                GeObjectType: mtObject.GeObjectType,
                GeometryType: mtObject.Type,
              };
              const imwork: ImMtWork = {
                Id: mtWork.Id,
                ObjectType: WorkService.MtWorkObjTypeName,
                Objects: [imobject],
              };
              generatedWorks.push(imwork);
              const taskQuery = WorkService.createTaskPersistQuery(
                [imwork],
                workType.taskTypes,
                (targetColumns, taskType) => this.addTaskFromQuery(mtWork, targetColumns, taskType),
              );
              if (taskQuery) {
                taskQueries.push(taskQuery);
              }
              works.push(mtWork);
            }
          },
        );
        for (const taskQuery of taskQueries) {
          query.Objects.push(...taskQuery.Objects);
          query.Parameters = ParameterHelper.combineParameterValues(query.Parameters, taskQuery.Parameters);
        }
        //HACK: Just subscribe, because await ...ToPromise() does not work
        this.persistenceService.executePersistObjectsQuery(query, acceptOfflineStorage).subscribe();

        for (const w of works) {
          const newWorkVM = new MobileWorkViewModel((await this.resAsync).get('Object'));
          await this.loadWork(newWorkVM, w, this.state.indexedMtPositionTypes);
          this.workViewPackage.allWorks.push(newWorkVM);
          this.setupWorkViewPackage(this.workViewPackage.allWorks);
        }
      }
    }
    return { generatedWorks: generatedWorks, excludedObjects: excludedObjects };
  }

  private async generateTicketForEntity(
    ticketCreation: MtTicketCreation,
    entity: DataEntity | undefined,
  ): Promise<void> {
    const res = await this.resAsync;
    const acceptOfflineStorage = this.dataService.getAcceptOfflineStorage();
    let isOnlyTicketWithGeometry: boolean = false;
    let ticketFeature: any = undefined;

    if (entity && entity.objectType === 'MtTicket' && entity.feature) {
      isOnlyTicketWithGeometry = true;
      ticketFeature = entity.feature;
    }

    let mtObject;
    if (entity && !isOnlyTicketWithGeometry) {
      mtObject = this.state?.mtObjects?.find((_) => _.GeObjectId === entity.id);
      let mtObjectId: string = undefined;
      if (!mtObject && !CacheStorageAdapter.useOfflineMode) {
        const mtObjBase = await this.imWorkService.ensureMtObjectFromEntity(entity);
        if (mtObjBase) {
          mtObjectId = mtObjBase.Id;
        } else {
          throw new Error(res.get('TicketNotCreated_DueTo_ObjectError'));
        }
      }

      if (mtObject || mtObjectId) {
        ticketCreation.Object = new ImMtObject();
        ticketCreation.Object.Id = mtObject ? mtObject.Id : mtObjectId;
      } else {
        throw new Error(res.get('Tickets-WO_Object_Created'));
      }
    }

    ticketCreation.Id = GUID.newGUID();
    let mtTicket: MtTicket;
    const ticketInsertQuery = this.createTicketPersistQuery(ticketCreation, ticketFeature, (targetColumns) => {
      mtTicket = this.addTicketFromQuery(targetColumns);
      mtTicket.Object = mtObject;
    });

    //save ticket
    await this.persistenceService.executePersistObjectsQuery(ticketInsertQuery, acceptOfflineStorage).toPromise();

    const newTicketVM: MobileTicketViewModel = new MobileTicketViewModel();
    await newTicketVM.load(this.persistenceService, this.objectSpaceService, mtTicket, this.mobileTicketResources);

    //save documents of ticket
    this.ensureTicketDocumentNames(ticketCreation);
    for (const document of ticketCreation.Documents) {
      this.addTicketDocument(newTicketVM, document);
    }

    //save tasks of tickets
    let reminderTask: MobileTicketTaskViewModel;
    for (const task of ticketCreation.Tasks) {
      const newTaskVM = newTicketVM.addTask(
        task.Bezeichnung,
        task.LfdNr,
        task.Status,
        task.StartDatum,
        task.StartZeit,
        task.EndDatum,
        task.EndZeit,
        task.IdRefMtResource,
        task.CreateActiveReminder,
      );
      if (task.CreateActiveReminder) {
        reminderTask = newTaskVM;
      }
    }

    try {
      await this.persistenceService.persistPendingChangesAsync(TicketConstants.PersistenceCategory_TicketDetail);
    } catch (err) {
      this.uploadFileDocumentsTaskInfos.forEach((v, k) => {
        this.frame.loadingScreenService.finishTask(v.id);
      });
      throw err;
    }

    if (reminderTask) {
      const title = res.get('Push-Messages-Send-New-Ticket-Message-Title', reminderTask.parentTicket.designation);

      let body: string = res.get('Push-Messages-Send-New-Ticket-Message-Body');
      if (reminderTask.parentTicket.priority) {
        const priorityDisplayText: string = this.ticketViewPackage.dringlichkeitListDataSource.dataItems.find(
          (v) => v.value === reminderTask.parentTicket.priority,
        ).description;
        body = res.get('Push-Messages-Send-New-Ticket-Message-Priority', priorityDisplayText) + '\n' + body;
      }

      await this.dataService.sendNotification(reminderTask.resource.login, title, body);
    }

    if (this.ticketViewPackage) {
      this.ticketViewPackage.allTickets.push(newTicketVM);
      this.setupTicketViewPackage(
        this.ticketViewPackage.statusListDataSource,
        this.ticketViewPackage.allTickets,
        this.ticketViewPackage.dringlichkeitListDataSource,
        this.ticketViewPackage.kategorieListDataSource,
        this.ticketViewPackage.currentUserResourceId,
      );
      this.sendMobileTickets();
    }
  }

  private ensureTicketDocumentNames(ticketCreation: MtTicketCreation) {
    for (let i = 0; i < ticketCreation.Documents.length; i++) {
      if (!ticketCreation.Documents[i].Designation) {
        ticketCreation.Documents[i].Designation = MTHelper.getUniqueDocumentName(
          ticketCreation.Documents[i].DataUrl.fileExtension,
          i,
        );
      }
    }
  }

  private addTicketDocument(newTicketVM: MobileTicketViewModel, doc: MtTicketCreationDocument): void {
    let newDocVM: MobileDocumentViewModel;
    if (doc.File) {
      newDocVM = newTicketVM.addDocumentByFile(doc.Designation, new Date(), doc.File);
      newDocVM.readFile = async (file) => {
        const sizeMB = file.size / (1024 * 1024); // Bytes → MB
        if (sizeMB > 5)
          this.uploadFileDocumentsTaskInfos.set(
            newDocVM,
            this.frame.loadingScreenService.addNewTask('Lade ' + newDocVM.designation + ' hoch...', true),
          );

        return (await ConvertHelper.readFileAsDataUrl(file)).base64;
      };
      newDocVM.afterPersisted = () => {
        if (this.uploadFileDocumentsTaskInfos.has(newDocVM)) {
          this.frame.loadingScreenService.finishTask(this.uploadFileDocumentsTaskInfos.get(newDocVM).id);
        }
      };
    } else {
      newDocVM = newTicketVM.addDocumentByDataUrl(doc.Designation, new Date(), doc.DataUrl);
    }
  }

  private getFeatureGeometryAsJson(feature: any, identifier: string): any {
    let ret = {};
    if (feature) {
      const geom = this.mapService.getCurrentView().transformGeometryToDataProjection(feature.getGeometry());
      if (geom) {
        ret = this.formPointGeometries(geom, identifier, feature.get('z_coord'));
      }
    }
    return ret;
  }

  formPointGeometries(point: any, identifier: string, z_coord: any): any {
    let coor = point.flatCoordinates;
    let ret = {
      Points: [
        {
          identifier: identifier,
          Feature: {
            Coordinate: {
              x: coor[0],
              y: coor[1],
              z: this.mapService.getZValue(z_coord),
            },
          },
        },
      ],
    };

    return ret;
  }

  private createTicketPersistQuery(
    ticketCreation: MtTicketCreation,
    ticketFeature: any | undefined = undefined,
    notifyTicketCreated?: (targetColumns: TargetColumnValue[]) => void,
  ): PersistObjectsModel {
    let result: PersistObjectsModel = new PersistObjectsModel();

    let objQueries: TargetObjectData[] = [];
    let ticketPersistquery: TargetObjectData = new TargetObjectData();

    ticketPersistquery.Mode = PersistMode.Insert;
    ticketPersistquery.ObjectKey = new ObjectKey();

    ticketPersistquery.ObjectKey.ObjectType = CreateTicketService.MtTicketObjectTypeName;
    const targetColumns: TargetColumnValue[] = [];
    const objectRefId: string | undefined = ticketCreation.Object?.Id;
    const geomIdentifier = 'POINT'; //To be further defined.
    const geometryAsJson = ticketFeature ? this.getFeatureGeometryAsJson(ticketFeature, geomIdentifier) : null;

    targetColumns.push(
      {
        Name: 'Id',
        Value: ticketCreation.Id,
      },
      {
        Name: 'Bezeichnung',
        Value: ticketCreation.Bezeichnung,
      },
      {
        Name: 'IdRefMtObject',
        Value: objectRefId,
      },
      {
        Name: 'Dringlichkeit',
        Value: ticketCreation.Dringlichkeit,
      },
      {
        Name: 'Kategorie',
        Value: ticketCreation.Kategorie,
      },
      {
        Name: 'Status',
        Value: ticketCreation.Status,
      },
      {
        Name: 'Herkunft',
        Value: ticketCreation.Herkunft,
      },
      {
        Name: 'Beschreibung',
        Value: ticketCreation.Beschreibung,
      },
      {
        Name: 'StartDatum',
        Value: WorkService.formatDate(ticketCreation.StartDatum, true),
      },
      {
        Name: 'EndDatum',
        Value: WorkService.formatDate(ticketCreation.EndDatum, true),
      },
      {
        Name: 'IdRefPersonBearbeiter',
        Value: ticketCreation.IdRefPersonBearbeiter,
      },
    );

    if (geometryAsJson) {
      targetColumns.push({ Name: 'Geometry__Json', Value: geometryAsJson });
    }

    ticketPersistquery.TargetColumns = targetColumns;
    objQueries.push(ticketPersistquery);

    if (notifyTicketCreated) {
      notifyTicketCreated(targetColumns);
    }
    result.Objects = [objQueries];
    return result;
  }

  private addObjectFromQuery(targetColumns: TargetColumnValue[], objects: MtObject[]): MtObject {
    const data = this.getDataFromTargetColumns(targetColumns);
    const newObject = new MtObject(data);
    if (!newObject.ObjectType) {
      this.objectSpaceService
        .getObjectType(WorkService.MtObjectObjTypeName)
        .toPromise()
        .then((_) => (newObject.ObjectType = _?.value));
    }
    if (!newObject.Type && !CompareHelper.isNullorEmpty(newObject['Typ'])) {
      let num: number = newObject['Typ'];
      let text: string;
      //HACK: Localize later
      if (num == 0) text = 'Knoten';
      else if (num == 1) text = 'Leitung';
      else if (num == 2) text = 'Fläche';
      newObject.Type = text;
    }
    if (!newObject.Street && newObject['IdRefStrasse']) {
      this.coreDataService
        .executeReadValueQuery({
          OPath: "Type(Strasse)[Id='" + newObject['IdRefStrasse'] + "'].GetValue(ref:Id)",
          Parameters: [],
        })
        .toPromise()
        .then((_) => (newObject.Street = <any>_));
    }
    objects.push(newObject);
    return newObject;
  }

  private addWorkFromQuery(mtObject: MtObject, targetColumns: TargetColumnValue[], workType: ImMtWorkType): MtWork {
    const data = this.getDataFromTargetColumns(targetColumns);
    const newWork = new MtWork(data, mtObject);
    if (!newWork.ObjectType) {
      this.objectSpaceService
        .getObjectType(WorkService.MtWorkObjTypeName)
        .toPromise()
        .then((_) => (newWork.ObjectType = _?.value));
    }
    if (!newWork.StateDisplayText) {
      let text: string;
      //HACK: Localize later
      if (newWork.State == 0) text = 'geplant';
      else if (newWork.State == 1) text = 'anstehend';
      else if (newWork.State == 2) text = 'beauftragt';
      else if (newWork.State == 3) text = 'teilerledigt';
      else if (newWork.State == 4) text = 'erledigt';
      newWork.StateDisplayText = text;
    }
    if (!mtObject.Works) {
      mtObject.Works = [];
    }
    mtObject.Works.push(newWork);
    return newWork;
  }

  private addTaskFromQuery(mtWork: MtWork, targetColumns: TargetColumnValue[], taskType: MtTaskType): MtTask {
    const data = this.getDataFromTargetColumns(targetColumns);
    const newTask = new MtTask(data);
    if (!mtWork.Tasks) {
      mtWork.Tasks = [];
    }
    mtWork.Tasks.push(newTask);
    return newTask;
  }

  private addTicketFromQuery(targetColumns: TargetColumnValue[]): MtTicket {
    const data = this.getDataFromTargetColumns(targetColumns);
    const newTicket = new MtTicket(data);

    if (!newTicket.ObjectType) {
      this.objectSpaceService
        .getObjectType(TicketConstants.ObjectTypeName_MtTicket)
        .toPromise()
        .then((_) => (newTicket.ObjectType = _?.value));
    }

    return newTicket;
  }

  private getDataFromTargetColumns(targetColumns: TargetColumnValue[]): any {
    const data: any = {};
    for (const c of targetColumns) {
      let value = c.Value;
      if (value?.Date) {
        value = value.Date;
      } else if (value?.Time) {
        value = value.Time;
      } else if (value?.DateTime) {
        value = value.DateTime;
      }
      if (value instanceof String) {
        value = value.toString();
      } else if (value instanceof Number) {
        value = Number.parseFloat(value.toString());
      }
      if (value === 'false' || value === 'true') {
        value = ConvertHelper.toBoolean(value);
      }
      if (c.Name === 'AllowMultiSelelectResults') {
        data['AllowMultiSelectResults'] = value;
      } else {
        data[c.Name] = value;
      }
    }
    return data;
  }

  private async getWorkTypeWithDateFilter(
    workTypes: ImMtWorkType[],
    objects: MtObject[],
    entity: DataEntity,
  ): Promise<ImMtWorkTypeSimple[]> {
    if (!entity) {
      return undefined;
    }

    const result = workTypes.map((_) => {
      return <ImMtWorkTypeSimple>{
        Id: _.Id,
        ObjectType: _.ObjectType,
        Description: _.designation,
      };
    });
    const currentDate = ConvertHelper.toISODateString(new Date());
    for (const o of objects) {
      if (o.GeObjectId === entity?.id) {
        for (const w of o.Works) {
          if (w.ScheduledExecutionDate === currentDate) {
            const index = result.findIndex((_) => _.Description === w.Designation);
            if (index >= 0) {
              result.splice(index, 1);
            }
          }
        }
      }
    }
    return result;
  }

  private async getWorkTypeWithDateAndObjectTypeDefinionFilter(
    workTypes: ImMtWorkType[],
    objects: MtObject[],
    entity: DataEntity,
  ): Promise<ImMtWorkTypeSimple[]> {
    if (!entity) {
      return undefined;
    }

    const rawResult = await Promise.all(
      workTypes.map(async (wt) => {
        if (await this.isEntityIncludedInObjectDefinition(wt, entity)) {
          return <ImMtWorkTypeSimple>{
            Id: wt.Id,
            ObjectType: wt.ObjectType,
            Description: wt.designation,
          };
        }
        return undefined;
      }),
    );

    const result = rawResult.filter((r): r is ImMtWorkTypeSimple => !!r);

    const currentDate = ConvertHelper.toISODateString(new Date());
    for (const o of objects) {
      if (o.GeObjectId === entity?.id) {
        for (const w of o.Works) {
          if (w.ScheduledExecutionDate === currentDate) {
            const index = result.findIndex((_) => _.Description === w.Designation);
            if (index >= 0) {
              result.splice(index, 1);
            }
          }
        }
      }
    }
    return result;
  }

  private async isEntityIncludedInObjectDefinition(workType: ImMtWorkType, entity: DataEntity): Promise<boolean> {
    let retIncluded: boolean = true;
    if (workType && entity && !!workType.objectTypeDefinition) {
      const objectTypeName: string = this.extractObjectTypeNameFromObjectTypeDefinition(workType.objectTypeDefinition);
      if (!!objectTypeName && !!entity.objectType) {
        retIncluded = await this.objectSpaceService.isMatch(objectTypeName, entity.objectType);
      }
    }

    return retIncluded;
  }

  private extractObjectTypeNameFromObjectTypeDefinition(objectTypeDefinition: string): string {
    const match = /^\s*type\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)/i.exec(objectTypeDefinition);
    return match ? match[1].trim() : '';
  }

  async showWorksByWorkIds(workIds: string[]) {
    await this.showWorksCore(null, workIds, WorkViewMode.AllFilteredByWorkIds);
  }
  async showWorks(entities: DataEntity[], mode: WorkViewMode) {
    await this.showWorksCore(entities, null, mode);
  }
  private async showWorksCore(entities: DataEntity[], workIds: string[], mode: WorkViewMode) {
    await this.refreshWorkViewPackage(entities, workIds, mode);

    if (
      (mode !== WorkViewMode.AllFilteredByWorkIds && this.workViewPackage.filterEntities.length > 0) ||
      (mode === WorkViewMode.AllFilteredByWorkIds && this.workViewPackage.filterWorkIds.length > 0)
    ) {
      if (this.frame.lib.isBottomSheetOpenByName(AppService.Widget_Works)) {
        this.sendMobileWorks();
      } else {
        const sub = this.frame.lib.onViewStateChanged().subscribe((state) => {
          if (
            state.type === ViewStateChangeArgType.BottomSheetPanelOpening ||
            state.type === ViewStateChangeArgType.BottomSheetPanelOpened
          ) {
            this.sendMobileWorks();
            sub.unsubscribe();
          }
        });
        this.frame.lib.openBottomSheetByName(AppService.Widget_Works);
      }
    } else {
      this.resAsync.then((resset) => this.openSnackBar(resset.get('Works-NoDataFound')));
    }
  }

  public async showTaskDetails() {
    this.frame.lib.openBottomSheetByName(AppService.Widget_Works);
  }

  async showTickets(entities: DataEntity[]) {
    await this.showTicketsCore(entities);
  }
  private async showTicketsCore(entities: DataEntity[]) {
    await this.refreshTicketViewPackage(entities);

    if (this.ticketViewPackage.filterEntities.length > 0) {
      const sub = this.frame.lib.onViewStateChanged().subscribe((state) => {
        if (state.type === ViewStateChangeArgType.BottomSheetPanelOpening) {
          this.sendMobileTickets();
          sub.unsubscribe();
        }
      });
      this.frame.lib.openBottomSheetByName(AppService.Widget_Tickets);
    } else {
      this.resAsync.then((resset) => this.openSnackBar(resset.get('Tickets-NoDataFound')));
    }
  }

  async newWork(entities: DataEntity[]) {
    this.imWorkService.showImWorkDialog(entities, await this.dataService.getOrderId());
  }

  async newTicket(entities: DataEntity[]) {
    const existsTickets: boolean = await this.dataService.hasMtTickets().toPromise();
    if (!existsTickets) {
      this.refreshMobileTickets(() => this.newTicketCore(entities), true);
    } else {
      this.newTicketCore(entities);
    }
  }

  private async newTicketCore(entities: DataEntity[]) {
    this.createTicketService.showCreateTicketDialog(
      entities,
      this.ticketViewPackage.dringlichkeitListDataSource,
      this.ticketViewPackage.kategorieListDataSource,
      this.ticketViewPackage.personnelResourcesDataSourceItems,
      this.ticketViewPackage.currentUserResourceId,
    );
  }

  private showImmediateWork(workId: string, show: boolean): void {
    if (this.workExistsById(workId)) {
      if (show) {
        this.showWorksByWorkIds([workId]);
      }
    } else if (show && workId) {
      this.refreshMobileWorks(() => this.showWorksByWorkIds([workId]), [workId], true);
    } else if (workId) {
      this.refreshMobileWorks(undefined, [workId], true);
    } else {
      this.refreshMobileWorks();
    }
  }

  private async showTicket(ticketDesignation: string, show: boolean): Promise<void> {
    if (show) {
      const res = await this.resAsync;
      const ticketCreationMsg = res.get('Ticket-Creation-Message', ticketDesignation);
      this.showTicketCreationMessageBox(ticketCreationMsg);
    } else {
      console.log('Ticket created successfully!');
    }
  }

  private workExistsById(workId: string): boolean {
    return !!this.workViewPackage?.allWorks?.find((_) => _.id === workId);
  }

  async removeWorkFromList(work: MobileWorkViewModel): Promise<void> {
    const idx = this.workViewPackage?.allWorks?.findIndex((_) => _.id === work.id);
    if (idx >= 0) {
      this.workViewPackage.allWorks.splice(idx, 1);
      this.setupWorkViewPackage(this.workViewPackage.allWorks);
      this.sendMobileWorks();
    }
  }

  async removeTicketFromList(ticket: MobileTicketViewModel): Promise<void> {
    const idx = this.ticketViewPackage?.allTickets?.findIndex((_) => _.id === ticket.id);
    if (idx >= 0) {
      this.ticketViewPackage.allTickets.splice(idx, 1);
      this.setupTicketViewPackage(
        this.ticketViewPackage.statusListDataSource,
        this.ticketViewPackage.allTickets,
        this.ticketViewPackage.dringlichkeitListDataSource,
        this.ticketViewPackage.kategorieListDataSource,
        this.ticketViewPackage.currentUserResourceId,
      );
      this.sendMobileTickets();
    }
  }

  private async refreshWorkViewPackage(objectInfos: DataEntity[], workIds: string[], mode: WorkViewMode) {
    this.workViewPackage.filterEntities = new Array<DataEntity>();
    this.workViewPackage.allWorksFilteredByEntities = new Array<MobileWorkViewModel>();
    this.workViewPackage.aggregatedWorks = new Array<MobileWorkViewModel>();
    this.workViewPackage.filterWorkIds = new Array<string>();
    if (workIds && workIds.length > 0) {
      this.workViewPackage.filterWorkIds = workIds;
    }

    if (objectInfos && objectInfos.length > 0 && this.workViewPackage.allWorks) {
      // Remove object duplicates
      CollectionHelper.removeDuplicates(objectInfos, (o1, o2) => CoreDataEntityHelper.equals(o1, o2));

      let generatedFromEntity: DataEntity = objectInfos[0];
      for (const objInfo of CollectionHelper.distinct(objectInfos, (_) => _.objectType + '_' + _.id)) {
        let found: boolean = false;
        for (const mWork of this.workViewPackage.allWorks) {
          if (
            mWork.filterableObjectId === objInfo.id &&
            (await this.objectSpaceService.isMatch(mWork.filterableObjectType, objInfo.objectType))
          ) {
            this.workViewPackage.allWorksFilteredByEntities.push(mWork);
            found = true;
            if (generatedFromEntity === objInfo && mWork.object?.objectId) {
              generatedFromEntity = new DataEntity(mWork.object.objectType, mWork.object.objectId);
            }
          }
        }
        if (found) {
          this.workViewPackage.filterEntities.push(objInfo);
        }
      }

      if (this.workViewPackage.filterEntities.length === 0) {
        this.workViewPackage.pathNameOfAggregation = 'n / a'; // will not happen
      } else {
        this.workViewPackage.pathNameOfAggregation =
          this.workViewPackage.allWorksFilteredByEntities[0].objectDesignation +
          '-' +
          this.workViewPackage.allWorksFilteredByEntities[this.workViewPackage.allWorksFilteredByEntities.length - 1]
            .objectDesignation;
      }

      if (mode === WorkViewMode.AggregatedByEntityCategory) {
        const groupedWorks: Map<string, MobileWorkViewModel[]> = ViewModelHelper.groupArray<MobileWorkViewModel>(
          this.workViewPackage.allWorksFilteredByEntities,
          'designation',
        );
        const taskCategories: {
          edges: boolean;
          startNode: boolean;
          endNode: boolean;
          midNodes: boolean;
          priority: number;
          keys: string[];
        }[] = [];
        groupedWorks.forEach((v, k) => {
          for (const w of v) {
            for (const t of w.tasks) {
              const category = {
                edges: t.relevantForEdges,
                startNode: t.relevantForStartNode,
                endNode: t.relevantForEndNode,
                midNodes: t.relevantForMidNodes,
              };
              let found = taskCategories.find(
                (_) =>
                  _.edges === category.edges &&
                  _.startNode === category.startNode &&
                  _.endNode === category.endNode &&
                  _.midNodes === category.midNodes,
              );
              if (!found) {
                found = {
                  edges: category.edges,
                  startNode: category.startNode,
                  endNode: category.endNode,
                  midNodes: category.midNodes,
                  priority: this.getCategoryPriority(
                    category.edges,
                    category.startNode,
                    category.endNode,
                    category.midNodes,
                  ),
                  keys: [],
                };
                taskCategories.push(found);
              }
              if (found.keys.indexOf(k) < 0) {
                found.keys.push(k);
              }
            }
          }
        });

        let singleAggregatedWork: MobileWorkViewModel;
        for (const category of taskCategories.sort((c1, c2) =>
          c1.priority < c2.priority ? -1 : c1.priority > c2.priority ? 1 : 0,
        )) {
          for (const e of Array.from(groupedWorks)) {
            let k = e[0];
            let v = e[1];
            if (category.keys.indexOf(k) >= 0) {
              let works = v.filter((_) =>
                _.tasks.find(
                  (t) =>
                    t.relevantForEdges === category.edges &&
                    t.relevantForStartNode === category.startNode &&
                    t.relevantForEndNode === category.endNode &&
                    t.relevantForMidNodes === category.midNodes,
                ),
              );

              const isCategoryMatch = (
                o: MobileObjectViewModel,
                edges: boolean,
                startNode: boolean,
                endNode: boolean,
                midNodes: boolean,
              ) =>
                this.isCategoryMatch(
                  o.mainTypeObjectType.name,
                  o.drawableObjectId,
                  objectInfos,
                  edges,
                  startNode,
                  endNode,
                  midNodes,
                );

              if (category.edges || category.startNode || category.endNode || category.midNodes) {
                works = v.filter((_) =>
                  isCategoryMatch(_.object, category.edges, category.startNode, category.endNode, category.midNodes),
                );
              }
              if (works.length) {
                const virtualWork = new MobileWorkViewModel((await this.resAsync).get('Object'));
                let virtualWorkDesignation = works[0].objectDesignation;
                if (virtualWorkDesignation !== works[works.length - 1].objectDesignation) {
                  virtualWorkDesignation += '-' + works[works.length - 1].objectDesignation;
                }

                virtualWork.loadVirtual(
                  works[0].designation,
                  virtualWorkDesignation,
                  null,
                  category.edges,
                  category.startNode,
                  category.endNode,
                  category.midNodes,
                  works[0].workTypeDurationMode,
                  works[0].duration,
                  generatedFromEntity,
                  works,
                );

                if (groupedWorks.size === 1) {
                  if (!singleAggregatedWork) {
                    singleAggregatedWork = new MobileWorkViewModel((await this.resAsync).get('Object'));
                    singleAggregatedWork.loadVirtual(
                      works[0].designation,
                      virtualWorkDesignation,
                      null,
                      false,
                      false,
                      false,
                      false,
                      works[0].workTypeDurationMode,
                      works[0].duration,
                      generatedFromEntity,
                      [],
                    );
                    this.workViewPackage.aggregatedWorks.push(singleAggregatedWork);
                  }
                  singleAggregatedWork.mergeWithWork(
                    virtualWork,
                    category.edges && works.findIndex((_) => isCategoryMatch(_.object, true, false, false, false)) >= 0,
                    category.startNode &&
                      works.findIndex((_) => isCategoryMatch(_.object, false, true, false, false)) >= 0,
                    category.endNode &&
                      works.findIndex((_) => isCategoryMatch(_.object, false, false, true, false)) >= 0,
                    category.midNodes &&
                      works.findIndex((_) => isCategoryMatch(_.object, false, false, false, true)) >= 0,
                  );
                } else {
                  this.workViewPackage.aggregatedWorks.push(virtualWork);
                }
              }
            }
          }
        }
      }
    }

    this.workViewPackage.mode = mode;
  }

  private async refreshTicketViewPackage(objectInfos: DataEntity[]) {
    this.ticketViewPackage.filterEntities = new Array<DataEntity>();
    this.ticketViewPackage.allTicketsFilteredByEntities = new Array<MobileTicketViewModel>();

    if (objectInfos && objectInfos.length > 0) {
      CollectionHelper.removeDuplicates(objectInfos, (o1, o2) => CoreDataEntityHelper.equals(o1, o2));

      for (const objInfo of objectInfos) {
        let found: boolean = false;
        for (const mTicket of this.ticketViewPackage.allTickets) {
          if (
            (mTicket.filterableObjectId === objInfo.id &&
              (await this.objectSpaceService.isMatch(mTicket.filterableObjectType, objInfo.objectType))) ||
            (mTicket.id === objInfo.id &&
              (await this.objectSpaceService.isMatch(mTicket.objectType, objInfo.objectType)))
          ) {
            this.ticketViewPackage.allTicketsFilteredByEntities.push(mTicket);
            found = true;
          }
        }

        if (found) {
          this.ticketViewPackage.filterEntities.push(objInfo);
        }
      }
    }
  }

  private isCategoryMatch(
    mainObjectTypeName: string,
    objectId: string,
    objectInfos: DataEntity[],
    edges: boolean,
    startNode: boolean,
    endNode: boolean,
    midNodes: boolean,
  ): boolean {
    if (ObjectTypesHelper.isNodeObjectType(mainObjectTypeName)) {
      if (
        (startNode && objectInfos[0].id === objectId) ||
        (endNode && objectInfos[objectInfos.length - 1].id === objectId)
      ) {
        return true;
      } else if (midNodes) {
        const index = objectInfos.findIndex((_) => _.id === objectId);
        return index > 0 && index < objectInfos.length - 1;
      } else {
        return false;
      }
    } else {
      return edges;
    }
  }

  private getCategoryPriority(edges: boolean, startNode: boolean, endNode: boolean, midNodes: boolean): number {
    let mainpriority = 0;
    if (!edges && !startNode && !endNode && !midNodes) {
      mainpriority += 4;
    } else {
      if (!startNode) {
        mainpriority += 1;
      }
      if (edges) {
        mainpriority += 2;
      }
    }

    let subpriority = 0;
    if (endNode) {
      subpriority += 1;
    }
    if (midNodes) {
      subpriority += 2;
    }

    return 3 * (mainpriority + 1) + subpriority;
  }

  public processNetworkWorks(entities: DataEntity[]) {
    if (entities && entities.length > 0) {
      if ((this.frame.frameConfig.settings.get(AppService.Setting_AggregatePathWorks) as BooleanSetting).value) {
        this.showWorks(entities, WorkViewMode.AggregatedByEntityCategory);
      } else {
        this.showWorks(entities, WorkViewMode.AllFilteredByEntities);
      }
    } else {
      this.resAsync.then((resset) => this.openSnackBar(resset.get('Works-NoObjects')));
    }
  }

  refreshMobileResourceTypes(afterRefreshMethod?: any) {
    this.dynSubscriptions.push(
      this.dataService.loadState().subscribe(
        (state) => {
          this.refreshMobileResourceTypesCore(state?.current, state?.last, afterRefreshMethod, false, true);
        },
        (error) => {
          console.error(error);
          this.refreshMobileResourceTypesCore();
        },
      ),
    );
  }

  private refreshMobileResourceTypesCore(
    currentstate?: State,
    laststate?: State,
    afterRefreshMethod?: any,
    closeBottomSheetAndRepublish?: boolean,
    isRefresh?: boolean,
  ) {
    this.queryMobileResourceTypes(currentstate, laststate, isRefresh)
      .catch((error) => {
        console.error(error);
        this.openSnackBar(this.frame.error.getFullErrorMessage(error, false), 2000);
      })
      .then(
        (mobileResourceTypes) => {
          if (closeBottomSheetAndRepublish) {
            this.frame.lib.closeBottomSheet();
          }

          this.setupResourceTypeViewPackage(mobileResourceTypes as MobileResourceTypeViewModel[]);

          if (closeBottomSheetAndRepublish) {
            this.sendMobileResourceTypes();
          }
          this.frame?.detectChanges();
          if (afterRefreshMethod) {
            afterRefreshMethod();
          }
          this.frame?.detectChanges();
        },
        (error) => {
          console.error(error);
          this.openSnackBar(this.frame.error.getFullErrorMessage(error, false), 2000);
        },
      );
  }

  private async queryMobileResourceTypes(
    currentstate: State,
    laststate: State,
    refreshCache?: boolean,
  ): Promise<MobileResourceTypeViewModel[]> {
    const loadMobileResourceTypesTask = from(this.resAsync.then((_) => _.get('Resources-LoadingMessage'))).pipe(
      map((txt) => this.frame.loadingScreenService.addNewTask(txt, true)),
    );
    const getMtResourceTypesQuery = currentstate?.mtResourceTypes
      ? of(currentstate?.mtResourceTypes)
      : CacheStorageAdapter.useOfflineMode && laststate?.mtResourceTypes
        ? of(laststate?.mtResourceTypes)
        : this.dataService.getMtResourceTypes(refreshCache);

    let loadProtocolTask: LoadProtocolTaskInfoSnapshot;
    const resSet = await this.resAsync;
    const orderId = await this.dataService.getOrderId();

    return this.libService.frame
      .getLoginInfo()
      .pipe(
        concatMap((info) => {
          if (!info?.isAnonymous && !info?.userName) {
            return of(<MobileResourceTypeViewModel[]>[]);
          } else {
            return forkJoin([loadMobileResourceTypesTask, getMtResourceTypesQuery]).pipe(
              concatMap((queryResults) => {
                loadProtocolTask = queryResults[0];
                const mtResourceTypes: MtResourceType[] = queryResults[1];
                if (mtResourceTypes) {
                  this.updateState({
                    mtObjects: undefined,
                    indexedMtPositionTypes: undefined,
                    mtTickets: undefined,
                    mtTicketResources: undefined,
                    mtTicketTaskStatusList: undefined,
                    mtResourceTypes: mtResourceTypes,
                    mtWorkTypes: undefined,
                    mtTicketDringlichkeitList: undefined,
                    mtTicketKategorieList: undefined,
                    mtCurrentUserResourceId: undefined,
                  });
                  return of(
                    mtResourceTypes.map((t) => {
                      const newResourceType = new MobileResourceTypeViewModel(resSet, orderId);
                      newResourceType.load(this.persistenceService, t, this.resourceListService);
                      return newResourceType;
                    }),
                  );
                } else {
                  return of(<MobileResourceTypeViewModel[]>[]);
                }
              }),
              map((resTypes) => resTypes.sort((a, b) => a.kind?.localeCompare(b.kind))),
              tap(() => {
                if (loadProtocolTask?.id) {
                  this.frame.loadingScreenService.finishTask(loadProtocolTask.id);
                }
              }),
            );
          }
        }),
      )
      .toPromise();
  }

  refreshMobileWorkTypes(afterRefreshMethod?: any) {
    this.dynSubscriptions.push(
      this.dataService.loadState().subscribe(
        (state) => {
          this.refreshMobileWorkTypesCore(state?.current, state?.last, afterRefreshMethod, true);
        },
        (error) => {
          console.error(error);
          this.refreshMobileWorkTypesCore();
        },
      ),
    );
  }

  private refreshMobileWorkTypesCore(
    currentstate?: State,
    laststate?: State,
    afterRefreshMethod?: any,
    isRefresh?: boolean,
  ) {
    this.queryMobileWorkTypes(currentstate, laststate, isRefresh)
      .catch((error) => {
        console.error(error);
        this.openSnackBar(this.frame.error.getFullErrorMessage(error, false), 2000);
      })
      .then(
        () => {
          this.frame?.detectChanges();
          if (afterRefreshMethod) {
            afterRefreshMethod();
          }
          this.frame?.detectChanges();
        },
        (error) => {
          console.error(error);
          this.openSnackBar(this.frame.error.getFullErrorMessage(error, false), 2000);
        },
      );
  }

  private async queryMobileWorkTypes(currentstate: State, laststate: State, refreshCache?: boolean): Promise<void> {
    const loadMobileWorkTypesTask = from(this.resAsync.then((_) => _.get('WorkTypes-LoadingMessage'))).pipe(
      map((txt) => this.frame.loadingScreenService.addNewTask(txt, true)),
    );
    const getMWorkTypesQuery = currentstate?.mtWorkTypes
      ? of(currentstate?.mtWorkTypes)
      : CacheStorageAdapter.useOfflineMode && laststate?.mtWorkTypes
        ? of(laststate?.mtWorkTypes)
        : this.dataService.getMtWorkTypes(refreshCache);

    let loadProtocolTask: LoadProtocolTaskInfoSnapshot;

    return this.libService.frame
      .getLoginInfo()
      .pipe(
        concatMap((info) => {
          if (!info?.isAnonymous && !info?.userName) {
            return of(null);
          } else {
            return forkJoin([loadMobileWorkTypesTask, getMWorkTypesQuery]).pipe(
              concatMap((queryResults) => {
                loadProtocolTask = queryResults[0];
                const mtWorkTypes: ImMtWorkType[] = queryResults[1];
                if (mtWorkTypes) {
                  this.updateState({
                    mtObjects: undefined,
                    indexedMtPositionTypes: undefined,
                    mtTickets: undefined,
                    mtTicketResources: undefined,
                    mtTicketTaskStatusList: undefined,
                    mtResourceTypes: undefined,
                    mtWorkTypes: mtWorkTypes,
                    mtTicketDringlichkeitList: undefined,
                    mtTicketKategorieList: undefined,
                    mtCurrentUserResourceId: undefined,
                  });
                  return of(null);
                } else {
                  return of(null);
                }
              }),
              tap(() => {
                if (loadProtocolTask?.id) {
                  this.frame.loadingScreenService.finishTask(loadProtocolTask.id);
                }
              }),
            );
          }
        }),
        map((_) => {
          return;
        }),
      )
      .toPromise();
  }

  private setupResourceTypeViewPackage(mobileResourceTypes: MobileResourceTypeViewModel[]): void {
    this.resourceTypeViewPackage = new ResourceTypeViewPackage();
    this.resourceTypeViewPackage.allResourceTypes = mobileResourceTypes;
    this.setupOfflineState();
    this.frame.refreshWidgets();
  }

  sendMobileResourceTypes() {
    if (this.resourceTypeViewPackage) {
      this.mobileResourceTypesSubject.next(this.resourceTypeViewPackage);
    }
  }

  onMobileResourceTypesSent(): Observable<ResourceTypeViewPackage> {
    return this.mobileResourceTypesSubject.asObservable();
  }
  //resources - end -

  //tickets - start -
  refreshMobileTickets(afterRefreshMethod?: any, closeBottomSheetAndRepublish?: boolean) {
    this.dynSubscriptions.push(
      this.dataService.loadState().subscribe(
        (state) => {
          this.refreshMobileTicketsCore(
            state?.current,
            state?.last,
            afterRefreshMethod,
            closeBottomSheetAndRepublish,
            true,
          );
        },
        (error) => {
          console.error(error);
          this.refreshMobileTicketsCore();
        },
      ),
    );
  }

  private refreshMobileTicketsCore(
    currentstate?: State,
    laststate?: State,
    afterRefreshMethod?: any,
    closeBottomSheetAndRepublish?: boolean,
    isRefresh?: boolean,
  ) {
    this.dynSubscriptions.push(
      this.queryMobileTickets(currentstate, laststate, isRefresh).subscribe(
        (mobileTickets) => {
          if (closeBottomSheetAndRepublish) {
            this.frame.lib.closeBottomSheet();
          }

          this.setupTicketViewPackage(
            mobileTickets.statusListDataSource,
            mobileTickets.tickets,
            mobileTickets.dringlichkeitListDataSource,
            mobileTickets.kategoryListDataSource,
            mobileTickets.currentUserResourceId,
          );

          if (closeBottomSheetAndRepublish) {
            this.sendMobileTickets();
          }
          this.frame?.detectChanges();
          if (afterRefreshMethod) {
            afterRefreshMethod();
          }
          this.frame?.detectChanges();
        },
        (error) => {
          console.error(error);
          this.openSnackBar(this.frame.error.getFullErrorMessage(error, false), 2000);
        },
      ),
    );
  }

  private queryMobileTickets(
    currentstate: State,
    laststate: State,
    refreshCache?: boolean,
  ): Observable<{
    statusListDataSource: ListDataSource;
    tickets: MobileTicketViewModel[];
    dringlichkeitListDataSource: ListDataSource;
    kategoryListDataSource: ListDataSource;
    currentUserResourceId: string;
  }> {
    return from(this.queryMobileTicketsAsync(currentstate, laststate, refreshCache));
  }

  private async queryMobileTicketsAsync(
    currentstate: State,
    laststate: State,
    refreshCache?: boolean,
  ): Promise<{
    statusListDataSource: ListDataSource;
    tickets: MobileTicketViewModel[];
    dringlichkeitListDataSource: ListDataSource;
    kategoryListDataSource: ListDataSource;
    currentUserResourceId: string;
  }> {
    const loadMobileTicketsTask = from(this.resAsync.then((_) => _.get('Tickets-LoadingMessage'))).pipe(
      map((txt) => this.frame.loadingScreenService.addNewTask(txt, true)),
    );

    const getMtTicketsQuery = currentstate?.mtTickets
      ? of(currentstate?.mtTickets)
      : CacheStorageAdapter.useOfflineMode && laststate?.mtTickets
        ? of(laststate?.mtTickets)
        : this.dataService.getMtTickets(refreshCache).pipe(
            catchError((error) => {
              console.log('Error fetching tickets:', error);
              return of([]);
            }),
          );

    const getMtTicketResourcesQuery = currentstate?.mtTicketResources
      ? of(currentstate?.mtTicketResources)
      : CacheStorageAdapter.useOfflineMode && laststate?.mtTicketResources
        ? of(laststate?.mtTicketResources)
        : this.dataService.getMtTicketResources(refreshCache).pipe(
            catchError((error) => {
              console.log('Error fetching ticket resources:', error);
              return of([]);
            }),
          );

    const getStatusListDataSourceQuery = currentstate?.mtTicketTaskStatusList
      ? of(currentstate?.mtTicketTaskStatusList)
      : CacheStorageAdapter.useOfflineMode && laststate?.mtTicketTaskStatusList
        ? of(laststate?.mtTicketTaskStatusList)
        : this.dataService.getReadListDataSourceResult('MtTicketTask', 'Status', refreshCache);

    const getTicketDringlichkeitListDataSourceQuery = currentstate?.mtTicketDringlichkeitList
      ? of(currentstate?.mtTicketDringlichkeitList)
      : CacheStorageAdapter.useOfflineMode && laststate?.mtTicketDringlichkeitList
        ? of(laststate?.mtTicketDringlichkeitList)
        : this.dataService.getReadListDataSourceResult('MtTicket', 'Dringlichkeit', refreshCache);

    const getTicketCategoryListDataSourceQuery = currentstate?.mtTicketKategorieList
      ? of(currentstate?.mtTicketKategorieList)
      : CacheStorageAdapter.useOfflineMode && laststate?.mtTicketKategorieList
        ? of(laststate?.mtTicketKategorieList)
        : this.dataService.getReadListDataSourceResult('MtTicket', 'Kategorie', refreshCache);

    const userResourceIdQuery = currentstate?.mtCurrentUserResourceId
      ? of(currentstate?.mtCurrentUserResourceId)
      : CacheStorageAdapter.useOfflineMode && laststate?.mtCurrentUserResourceId
        ? of(laststate?.mtCurrentUserResourceId)
        : from(this.dataService.getCurrentUserResourceId());

    let loadProtocolTask: LoadProtocolTaskInfoSnapshot;
    const resSet = await this.resAsync;

    return this.libService.frame
      .getLoginInfo()
      .pipe(
        concatMap((info) => {
          if (!info?.isAnonymous && !info?.userName) {
            return of({
              statusListDataSource: <ListDataSource>null,
              tickets: <MobileTicketViewModel[]>[],
              dringlichkeitListDataSource: <ListDataSource>null,
              kategoryListDataSource: <ListDataSource>null,
              currentUserResourceId: <string>null,
            });
          } else {
            return forkJoin([
              loadMobileTicketsTask,
              getMtTicketsQuery,
              getMtTicketResourcesQuery,
              getStatusListDataSourceQuery,
              getTicketDringlichkeitListDataSourceQuery,
              getTicketCategoryListDataSourceQuery,
            ]).pipe(
              concatMap((queryResults) => {
                loadProtocolTask = queryResults[0];
                const mtTickets: MtTicket[] = queryResults[1];

                if (mtTickets) {
                  const mtResources: MtResource[] = queryResults[2];
                  const statusListDataSourceResult: ReadListDataSourceResult = queryResults[3];
                  const dringlichkeitListDataSourceResult: ReadListDataSourceResult = queryResults[4];
                  const kategorieListDataSourceResult: ReadListDataSourceResult = queryResults[5];

                  this.mobileTicketResources = mtResources
                    .map((r) => {
                      const newResource = new MobileResourceViewModel(resSet, undefined);
                      newResource.load(undefined, r, undefined, undefined);
                      return newResource;
                    })
                    .sort((a, b) => a.name.localeCompare(b.name));

                  return userResourceIdQuery.pipe(
                    map((userResourceIdResult) => {
                      const retObj = {
                        statusListDataSource: new ListDataSource(statusListDataSourceResult),
                        tickets: mtTickets.map((t) => {
                          const newTicket = new MobileTicketViewModel();
                          newTicket.load(
                            this.persistenceService,
                            this.objectSpaceService,
                            t,
                            this.mobileTicketResources,
                          );
                          return newTicket;
                        }),
                        dringlichkeitListDataSource: new ListDataSource(dringlichkeitListDataSourceResult),
                        kategoryListDataSource: new ListDataSource(kategorieListDataSourceResult),
                        currentUserResourceId: userResourceIdResult,
                      };

                      this.updateState({
                        mtObjects: undefined,
                        indexedMtPositionTypes: undefined,
                        mtTickets: mtTickets,
                        mtTicketResources: mtResources,
                        mtTicketTaskStatusList: statusListDataSourceResult,
                        mtResourceTypes: undefined,
                        mtWorkTypes: undefined,
                        mtTicketDringlichkeitList: dringlichkeitListDataSourceResult,
                        mtTicketKategorieList: kategorieListDataSourceResult,
                        mtCurrentUserResourceId: userResourceIdResult,
                      });
                      return retObj;
                    }),
                  );
                } else {
                  return of({
                    statusListDataSource: <ListDataSource>null,
                    tickets: <MobileTicketViewModel[]>[],
                    dringlichkeitListDataSource: <ListDataSource>null,
                    kategoryListDataSource: <ListDataSource>null,
                    currentUserResourceId: <string>null,
                  });
                }
              }),
              tap(() => {
                if (loadProtocolTask?.id) {
                  this.frame.loadingScreenService.finishTask(loadProtocolTask.id);
                }
              }),
            );
          }
        }),
      )
      .toPromise();
  }

  private setupTicketViewPackage(
    statusListDataSource: ListDataSource,
    mobileTickets: MobileTicketViewModel[],
    dringlichkeitListDataSource: ListDataSource,
    kategorieListDataSource: ListDataSource,
    currentUserResourceId: string,
  ): void {
    this.ticketViewPackage = new TicketViewPackage();
    this.ticketViewPackage.statusListDataSource = statusListDataSource;
    this.ticketViewPackage.allTickets = mobileTickets;
    this.ticketViewPackage.dringlichkeitListDataSource = dringlichkeitListDataSource;
    this.ticketViewPackage.kategorieListDataSource = kategorieListDataSource;
    this.ticketViewPackage.currentUserResourceId = currentUserResourceId;

    this.ticketViewPackage.personnelResourcesDataSourceItems = this.mobileTicketResources.map(
      (res) => new ListDataSourceItem({ Value: res.id, Description: res.name }),
    );

    this.setupOfflineState();
    this.frame.refreshWidgets();
  }

  sendMobileTickets() {
    if (this.ticketViewPackage) {
      this.mobileTicketsSubject.next(this.ticketViewPackage);
    }
  }

  onMobileTicketsSent(): Observable<TicketViewPackage> {
    return this.mobileTicketsSubject.asObservable();
  }

  openSnackBar(text: string, durationInMilliseconds: number = 2000) {
    setTimeout(() => {
      this.snackBar.open(text, null, {
        duration: durationInMilliseconds,
        verticalPosition: 'top',
      });
    }, 1);
  }

  public dispose(): void {
    this.dynSubscriptions.forEach((element) => {
      element.unsubscribe();
    });
  }

  public async saveOfflineData(isManual: boolean): Promise<void> {
    let MsgSave: string = (await this.resAsync).get('SyncOfflineData');
    const MsgQuestionSave = (await this.resAsync).get('SyncOfflineSaveYesNo');
    let save: boolean = true;

    if (isManual) {
      const result: DialogResult = await this.showMessageBox(MsgQuestionSave);
      save = result === DialogResult.Yes;
    }

    if (save) {
      this.syncOfflineDataTask = this.frame.loadingScreenService.addNewTask(MsgSave, true);

      this.persistenceService.cacheAdapter
        .doProcessStoredQueries()
        .catch((err) => this.frame.error.publish(err))
        .then(() => {
          if (isManual && this.syncOfflineDataTask) {
            this.frame.loadingScreenService.finishTask(this.syncOfflineDataTask.id);
          }
        });

      if (!isManual) {
        if (this.syncOfflineDataTask)
          setTimeout(() => {
            this.frame.loadingScreenService.finishTask(this.syncOfflineDataTask.id);
          }, 2000);
      }
    }
  }

  public showMessageBox(message: string): Promise<DialogResult> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Warning;
    settings.button = MessageBoxButtons.YesNo;
    settings.message = message;
    settings.caption = '';
    return this.messageBoxService
      .showMessageBox(settings)
      .pipe(map((dialogResult) => (<any>dialogResult).result))
      .toPromise();
  }

  public showTicketCreationMessageBox(message: string): Promise<DialogResult> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Info;
    settings.button = MessageBoxButtons.OK;
    settings.message = message;
    settings.caption = '';
    return this.messageBoxService
      .showMessageBox(settings)
      .pipe(map((dialogResult) => (<any>dialogResult).result))
      .toPromise();
  }

  public async exportWorkData(orderDesignation: string, offlineChangesOnly: boolean): Promise<void> {
    try {
      const obj = this.cloneState(this.state);
      let filename;
      if (!orderDesignation) {
        orderDesignation = 'data';
      }
      if (offlineChangesOnly) {
        filename = orderDesignation + '-offline';
        const queries = await this.persistenceService.cacheAdapter.getStoredQueries();
        const objects: TargetObjectData[] = [];
        if (queries) {
          for (const q of queries) {
            if (q instanceof PersistObjectsModel && q.Objects?.length) {
              for (const arr of q.Objects) {
                objects.push(...arr);
              }
            } else if (q instanceof PersistObjectModel && q.Object) {
              objects.push(q.Object);
            }
          }
        }
        this.filterState(obj, objects);
      } else {
        filename = orderDesignation;
      }

      ObjectHelper.replaceValuesDeep(obj, null, undefined);
      ObjectHelper.replaceValuesDeep(obj, NaN, undefined);

      let convertFilename = filename.replace(/ /g, '_');
      this.downloadJsonFile(obj, convertFilename + '.json');
    } catch (err) {
      this.frame.error.publish(err);
    }
  }

  private filterState(state: State, objects: TargetObjectData[]): void {
    if (!state) {
      return;
    }
    const idMap: Map<string, unknown> = new Map<string, unknown>();
    if (objects) {
      for (const o of objects) {
        let id = o?.TargetColumns?.find((_) => _.Name === 'Id')?.Value;
        if (!id) {
          id = o?.ObjectKey?.Id;
        }
        if (id && o.Mode != PersistMode.Delete) {
          idMap.set(id, o);
        }
      }
    }

    if (state.mtObjects) {
      let hasAnyObjectChanges = false;
      for (const o of Array.from(state.mtObjects)) {
        let hasObjectChanges: boolean = !!idMap.get(o.Id);
        if (o.Works) {
          for (const w of Array.from(o.Works)) {
            let hasWorkChanges = !!idMap.get(w.Id);
            if (w.Tasks) {
              for (const wt of Array.from(w.Tasks)) {
                let hasTaskChanges = !!idMap.get(wt.Id);
                if (wt.Results) {
                  for (const r of Array.from(wt.Results)) {
                    let hasResultChanges = !!idMap.get(r.Id);
                    if (r.Documents) {
                      for (const d of Array.from(r.Documents)) {
                        let hasDocumentChanges = !!idMap.get(d.Id);
                        if (hasDocumentChanges) {
                          hasResultChanges = true;
                        } else {
                          CollectionHelper.removeItem(r.Documents, d);
                        }
                      }
                    }
                    if (hasResultChanges) {
                      hasTaskChanges = true;
                    } else {
                      CollectionHelper.removeItem(wt.Results, r);
                    }
                  }
                }
                if (hasTaskChanges) {
                  hasWorkChanges = true;
                } else {
                  CollectionHelper.removeItem(w.Tasks, wt);
                }
              }
            }
            if (hasWorkChanges) {
              hasObjectChanges = true;
            } else {
              CollectionHelper.removeItem(o.Works, w);
            }
          }
        }
        if (hasObjectChanges) {
          hasAnyObjectChanges = true;
        } else {
          CollectionHelper.removeItem(state.mtObjects, o);
        }
      }
      if (!hasAnyObjectChanges) {
        state.mtObjects = undefined;
      }
    }

    if (state.mtTickets) {
      let hasAnyTicketChanges = false;
      for (const t of Array.from(state.mtTickets)) {
        let hasTicketChanges: boolean = !!idMap.get(t.Id);
        if (t.TicketTasks) {
          for (const tt of Array.from(t.TicketTasks)) {
            let hasTicketTaskChanges: boolean = !!idMap.get(tt.Id);
            if (tt.Comments) {
              for (const c of Array.from(tt.Comments)) {
                let hasCommentChanges: boolean = !!idMap.get(c.Id);
                if (hasCommentChanges) {
                  hasTicketTaskChanges = true;
                } else {
                  CollectionHelper.removeItem(tt.Comments, c);
                }
              }
            }
            if (tt.Documents) {
              for (const d of Array.from(tt.Documents)) {
                let hasDocumentChanges: boolean = !!idMap.get(d.Id);
                if (hasDocumentChanges) {
                  hasTicketTaskChanges = true;
                } else {
                  CollectionHelper.removeItem(tt.Documents, d);
                }
              }
            }

            if (hasTicketTaskChanges) {
              hasTicketChanges = true;
            } else {
              CollectionHelper.removeItem(t.TicketTasks, tt);
            }
          }
        }
        if (hasTicketChanges) {
          hasAnyTicketChanges = true;
        } else {
          CollectionHelper.removeItem(state.mtTickets, t);
        }
      }
      if (!hasAnyTicketChanges) {
        state.mtTickets = undefined;
      }
    }

    if (state.mtResourceTypes) {
      let hasAnyResourceTypeChanges = false;
      for (const rt of Array.from(state.mtResourceTypes)) {
        let hasResourceTypeChanges = false;
        if (rt.Resources) {
          let hasAnyResourceChanges = false;
          for (const r of Array.from(rt.Resources)) {
            let hasResourceChanges = !!idMap.get(r.Id);
            if (r.ResourceEvents) {
              for (const re of Array.from(r.ResourceEvents)) {
                let hasResourceEventChanges = !!idMap.get(re.Id);
                if (hasResourceEventChanges) {
                  hasResourceChanges = true;
                } else {
                  CollectionHelper.removeItem(r.ResourceEvents, re);
                }
              }
            }
            if (hasResourceChanges) {
              hasAnyResourceChanges = true;
            } else {
              CollectionHelper.removeItem(rt.Resources, r);
            }
          }
          if (hasAnyResourceChanges) {
            hasResourceTypeChanges = true;
          } else {
            rt.Resources = undefined;
          }
        }
        if (rt.ResourceEventTypes) {
          let hasAnyResourceEventTypeChanges = false;
          for (const ret of Array.from(rt.ResourceEventTypes)) {
            let hasResourceEventTypeChanges = !!idMap.get(ret.Id);
            if (hasResourceEventTypeChanges) {
              hasAnyResourceEventTypeChanges = true;
            } else {
              CollectionHelper.removeItem(rt.ResourceEventTypes, ret);
            }
          }
          if (hasAnyResourceEventTypeChanges) {
            hasResourceTypeChanges = true;
          } else {
            rt.ResourceEventTypes = undefined;
          }
        }
        if (hasResourceTypeChanges) {
          hasAnyResourceTypeChanges = true;
        } else {
          CollectionHelper.removeItem(state.mtResourceTypes, rt);
        }
      }
      if (!hasAnyResourceTypeChanges) {
        state.mtResourceTypes = undefined;
      }
    }
  }

  private cloneState(state: State): State {
    if (!state) {
      return undefined;
    }
    const copy: State = {
      mtObjects: undefined,
      mtTickets: undefined,
      mtTicketResources: undefined,
      mtResourceTypes: ObjectHelper.cloneDeep(state.mtResourceTypes),
      indexedMtPositionTypes: undefined,
      mtCurrentUserResourceId: undefined,
      mtTicketDringlichkeitList: undefined,
      mtTicketKategorieList: undefined,
      mtTicketTaskStatusList: undefined,
      mtWorkTypes: undefined,
    };
    if (state.mtObjects) {
      copy.mtObjects = [];
      for (const o of state.mtObjects) {
        const newo = ObjectHelper.clone(o);
        copy.mtObjects.push(newo);
        if (o.Works) {
          newo.Works = [];
          for (const w of o.Works) {
            const neww = ObjectHelper.clone(w);
            neww.Object = undefined;
            newo.Works.push(neww);
            if (w.Tasks) {
              neww.Tasks = ObjectHelper.cloneArray(w.Tasks);
            }
          }
        }
      }
    }
    if (state.mtTickets) {
      copy.mtTickets = [];
      for (const t of state.mtTickets) {
        const newt = ObjectHelper.clone(t);
        if (newt.Object) {
          newt.Object = ObjectHelper.clone(newt.Object);
          newt.Object.Works = undefined;
        }
        copy.mtTickets.push(newt);
        if (t.TicketTasks) {
          newt.TicketTasks = ObjectHelper.cloneArray(t.TicketTasks);
        }
      }
    }

    return copy;
  }

  private downloadJsonFile(data: object, convertFilename: string): void {
    const jsonString = JSON.stringify(data, null, 2);

    if (window['deviceReady']) {
      this.checkDownloadPermission(new Blob([jsonString], { type: 'application/octet-stream' }), convertFilename);
      this.openSnackBar('die ' + convertFilename + ' wurde im Download-Ordner gespeichert.');
    } else {
      const blob = new Blob([jsonString], { type: 'application/json' });

      let url: string;
      let a: HTMLAnchorElement;
      try {
        url = URL.createObjectURL(blob);
        a = document.createElement('a');
        a.href = url;
        a.download = convertFilename;

        document.body.appendChild(a);
        a.click();
      } finally {
        if (a) {
          document.body.removeChild(a);
        }
        if (url) {
          URL.revokeObjectURL(url);
        }
      }
    }
  }

  // Need to be moved to a cordova service
  private checkDownloadPermission(blob: Blob, convertFilename: string): void {
    const permissions = cordova.plugins.permissions;
    const permissionList = [permissions.WRITE_EXTERNAL_STORAGE, permissions.READ_EXTERNAL_STORAGE];
    permissions.checkPermission(permissions.WRITE_EXTERNAL_STORAGE, (status) => {
      if (status.hasPermission) {
        this.cordovaDownloadFile(blob, convertFilename);
      } else {
        permissions.requestPermissions(
          permissionList,
          (succes) => {
            this.cordovaDownloadFile(blob, convertFilename);
          },
          (error) => {
            console.log(error);
          },
        );
      }
    });
  }

  // Need to be moved to a cordova service
  private cordovaDownloadFile(blob: Blob, convertFilename: string) {
    const isAndroid = navigator.userAgent.includes('Android');
    if (isAndroid) {
      const fileSystem = window['requestFileSystem'] || window['webkitRequestFileSystem'];
      const targetPath = cordova.file.externalRootDirectory + 'Download/';
      window['resolveLocalFileSystemURL'](
        targetPath,
        async (dirEntry) => {
          fileSystem(
            window['PERSISTENT'],
            1024 * 1024,
            (fs) => {
              dirEntry.getFile(
                convertFilename,
                { create: true, exclusive: false },
                (fileEntry) => {
                  fileEntry.createWriter((fileWriter) => {
                    fileWriter.onwriteend = () => {};
                    fileWriter.write(blob);
                  });
                },
                (error) => console.error(error),
              );
            },
            (error) => console.error(error),
          );
        },
        (error) => console.error(error),
      );
    } else {
      const fileSystem = window['requestFileSystem'] || window['webkitRequestFileSystem'];
      fileSystem(
        window['PERSISTENT'],
        1024 * 1024,
        (fs) => {
          fs.root.getFile(
            convertFilename,
            { create: true, exclusive: false },
            (fileEntry) => {
              fileEntry.createWriter((fileWriter) => {
                fileWriter.onwriteend = () => {};
                fileWriter.write(blob);
              });
            },
            (error) => console.error(error),
          );
        },
        (error) => console.error(error),
      );
    }
  }
}
